import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChefHat,
  Coffee,
  Cookie,
  Flame,
  Moon,
  Salad,
  Sparkles,
  UtensilsCrossed,
  Wheat,
} from 'lucide-react';
import type {
  DraftGenerationBlueprintSelection,
} from '@/types/meal-plan';
import { cn } from '@/lib/utils';

interface ShowcaseMeal {
  key: string;
  dayNumber: number;
  mealName: string;
  mealTime?: string;
  mealType?: string;
  title: string;
  cuisine?: string;
  primaryProtein?: string;
  cookingMethod?: string;
  dishFormat?: string;
  targetCalories?: number;
  targetProtein?: number;
  targetCarbohydrate?: number;
  targetFat?: number;
}

interface MealGenerationShowcaseProps {
  /** Heading node, e.g. "Generating meals for <ClientLink/>". */
  heading: React.ReactNode;
  subheading?: string;
  blueprint?: DraftGenerationBlueprintSelection | null;
  /** 0 - 100 progress driven by the API. */
  progress: number;
  /** Short rotating status line (e.g. "Picking recipes…"). */
  phaseLabel: string;
}

const SLIDE_INTERVAL_MS = 3200;

const prettify = (value?: string): string => {
  if (!value) return '';
  return value
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) =>
      part.length <= 3 && part === part.toUpperCase()
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
    )
    .join(' ');
};

const formatTime = (value?: string): string => {
  if (!value) return '';
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return value;
  let hours = Number(match[1]);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${period}`;
};

const mealTypeIcon = (mealType?: string): React.ComponentType<{ className?: string }> => {
  const type = (mealType || '').toLowerCase();
  if (type.includes('breakfast')) return Coffee;
  if (type.includes('snack')) return Cookie;
  if (type.includes('lunch')) return Salad;
  if (type.includes('dinner')) return Moon;
  return UtensilsCrossed;
};

const flattenBlueprint = (blueprint?: DraftGenerationBlueprintSelection | null): ShowcaseMeal[] => {
  if (!blueprint?.days?.length) return [];

  const meals: ShowcaseMeal[] = [];
  blueprint.days.forEach((day) => {
    (day.meals || []).forEach((meal) => {
      const title = meal.title || meal.recipeOptions?.[0]?.title;
      if (!title) return;
      meals.push({
        key: `${day.dayNumber}-${meal.mealOrder}`,
        dayNumber: day.dayNumber,
        mealName: meal.mealName,
        mealTime: meal.mealTime,
        mealType: meal.mealType,
        title,
        cuisine: meal.cuisine,
        primaryProtein: meal.primaryProtein,
        cookingMethod: meal.cookingMethod,
        dishFormat: meal.dishFormat,
        targetCalories: meal.targetCalories,
        targetProtein: meal.targetProtein,
        targetCarbohydrate: meal.targetCarbohydrate,
        targetFat: meal.targetFat,
      });
    });
  });
  return meals;
};

const MacroStat: React.FC<{ label: string; value?: number; unit: string }> = ({ label, value, unit }) => (
  <div className="flex flex-col items-center rounded-xl bg-white/10 px-3 py-2 backdrop-blur-sm">
    <span className="text-base font-semibold leading-none text-white">
      {value != null ? Math.round(value) : '—'}
      <span className="ml-0.5 text-[10px] font-medium text-white/70">{unit}</span>
    </span>
    <span className="mt-1 text-[10px] font-medium uppercase tracking-wide text-white/60">{label}</span>
  </div>
);

const Chip: React.FC<{ children: React.ReactNode; icon?: React.ComponentType<{ className?: string }> }> = ({
  children,
  icon: Icon,
}) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium text-white/90 backdrop-blur-sm">
    {Icon ? <Icon className="h-3 w-3 text-white/80" /> : null}
    {children}
  </span>
);

export const MealGenerationShowcase: React.FC<MealGenerationShowcaseProps> = ({
  heading,
  subheading,
  blueprint,
  progress,
  phaseLabel,
}) => {
  const meals = useMemo(() => flattenBlueprint(blueprint), [blueprint]);
  const [activeIndex, setActiveIndex] = useState(0);
  const hoveringRef = useRef(false);

  // Keep the spotlight index within bounds as meals stream in.
  useEffect(() => {
    if (meals.length === 0) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex((prev) => (prev >= meals.length ? meals.length - 1 : prev));
  }, [meals.length]);

  // Auto-advance the spotlight slideshow.
  useEffect(() => {
    if (meals.length <= 1) return;
    const intervalId = window.setInterval(() => {
      if (hoveringRef.current) return;
      setActiveIndex((prev) => (prev + 1) % meals.length);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [meals.length]);

  const activeMeal = meals[activeIndex];
  const boundedProgress = Math.min(100, Math.max(0, progress));
  const hasMeals = meals.length > 0;

  return (
    <section className="relative flex min-h-[480px] w-full max-w-[820px] flex-col overflow-hidden rounded-3xl bg-gradient-to-br from-sky-500 via-cyan-600 to-teal-600 px-6 py-8 text-white shadow-xl sm:px-9 sm:py-10">
      {/* Decorative glow */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />

      <div className="relative z-10 flex flex-col items-center text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white/90 backdrop-blur-sm">
          <Sparkles className="h-3.5 w-3.5 animate-pulse" />
          AI is building the menu
        </span>
        <h2 className="mt-4 text-[22px] font-semibold leading-tight sm:text-[24px]">{heading}</h2>
        {subheading ? <p className="mt-1.5 max-w-[520px] text-sm text-white/85">{subheading}</p> : null}
      </div>

      {hasMeals ? (
          /* Spotlight slideshow card */
          <div
            className="relative z-10 mx-auto mt-7 w-full max-w-[560px]"
            onMouseEnter={() => {
              hoveringRef.current = true;
            }}
            onMouseLeave={() => {
              hoveringRef.current = false;
            }}
          >
            <div className="rounded-2xl border border-white/20 bg-white/10 p-5 shadow-lg backdrop-blur-md">
              {activeMeal ? (
                <div key={activeMeal.key} className="animate-fade-in">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                        {React.createElement(mealTypeIcon(activeMeal.mealType), {
                          className: 'h-5 w-5 text-white',
                        })}
                      </span>
                      <div className="text-left">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-white/70">
                          {meals.some((m) => m.dayNumber !== activeMeal.dayNumber)
                            ? `Day ${activeMeal.dayNumber} · `
                            : ''}
                          {activeMeal.mealName}
                        </p>
                        {activeMeal.mealTime ? (
                          <p className="text-[11px] text-white/60">{formatTime(activeMeal.mealTime)}</p>
                        ) : null}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/20 px-2.5 py-1 text-[11px] font-semibold text-emerald-50">
                      <Check className="h-3 w-3" />
                      Selected
                    </span>
                  </div>

                  <h3 className="mt-4 text-left text-[19px] font-semibold leading-snug text-white">
                    {activeMeal.title}
                  </h3>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {activeMeal.cuisine ? <Chip icon={ChefHat}>{prettify(activeMeal.cuisine)}</Chip> : null}
                    {activeMeal.primaryProtein ? (
                      <Chip icon={Flame}>{prettify(activeMeal.primaryProtein)}</Chip>
                    ) : null}
                    {activeMeal.cookingMethod ? <Chip>{prettify(activeMeal.cookingMethod)}</Chip> : null}
                    {activeMeal.dishFormat ? <Chip icon={Wheat}>{prettify(activeMeal.dishFormat)}</Chip> : null}
                  </div>

                  <div className="mt-4 grid grid-cols-4 gap-2">
                    <MacroStat label="kcal" value={activeMeal.targetCalories} unit="" />
                    <MacroStat label="protein" value={activeMeal.targetProtein} unit="g" />
                    <MacroStat label="carbs" value={activeMeal.targetCarbohydrate} unit="g" />
                    <MacroStat label="fat" value={activeMeal.targetFat} unit="g" />
                  </div>
                </div>
              ) : null}
            </div>

            {/* Slide indicators */}
            {meals.length > 1 ? (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
                {meals.map((meal, index) => (
                  <button
                    key={meal.key}
                    type="button"
                    aria-label={`Show ${meal.title}`}
                    onClick={() => setActiveIndex(index)}
                    className={cn(
                      'h-1.5 rounded-full transition-all duration-300',
                      index === activeIndex ? 'w-7 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70',
                    )}
                  />
                ))}
              </div>
            ) : null}
          </div>
      ) : (
        <div className="relative z-10 mx-auto mt-10 flex flex-col items-center">
          <div className="h-14 w-14 animate-spin rounded-full border-4 border-white/30 border-t-white" />
          <p className="mt-5 max-w-[440px] text-center text-sm text-white/85">
            Reading your client&apos;s profile and selecting evidence-based recipes…
          </p>
        </div>
      )}

      {/* Progress footer */}
      <div className="relative z-10 mt-auto pt-8">
        <div className="mx-auto flex max-w-[560px] items-center justify-between text-[12px] font-medium text-white/80">
          <span>{hasMeals ? `${meals.length} ${meals.length === 1 ? 'recipe' : 'recipes'} selected` : phaseLabel}</span>
          <span>{Math.round(boundedProgress)}%</span>
        </div>
        <div className="mx-auto mt-2 h-2.5 w-full max-w-[560px] overflow-hidden rounded-full bg-white/20">
          <div
            className="h-full rounded-full bg-white transition-[width] duration-700 ease-out"
            style={{ width: `${boundedProgress}%` }}
          />
        </div>
        <p className="mx-auto mt-2.5 max-w-[560px] text-center text-[12px] text-white/70">
          {hasMeals ? 'Preparing full ingredients, instructions & nutrition…' : phaseLabel}
        </p>
      </div>
    </section>
  );
};

export default MealGenerationShowcase;
